import React, { useState, useEffect } from 'react';
import { X, ClipboardPlus, Database, ScrollText } from 'lucide-react';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';

// Development pages that show the help icon
const DEV_PAGES = ['development-dashboard', 'task-log', 'rev-log', 'dev-plan', 'schema-viewer', 'claude-notes', 'sample-table-view', 'standard-view-config'];

// Help content for each dev page - structured with sections
const PAGE_HELP = {
  'development-dashboard': {
    title: 'Development Dashboard',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Central hub for all development tools',
          'Visual summary of each tool with donut charts',
          'Click any tile to navigate to that page'
        ]
      },
      {
        heading: 'Tiles',
        items: [
          'Current Sprint - Task statuses donut (Pending/In Progress/Validate/Complete)',
          'Dev Plan - Step statuses donut (Not Started/In Progress/Complete)',
          'Rev Log - Sparkline showing minor revs, vertical lines at major versions',
          'Schema Viewer - Badge counts for tables and columns',
          'Navigation - Active vs future nav items donut',
          'Module Templates - Configured vs unconfigured tables donut'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Refresh - Reload all dashboard statistics'
        ]
      }
    ]
  },
  'task-log': {
    title: 'Task Log',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Task management for development work',
          'Tasks move through status workflow from entry to completion'
        ]
      },
      {
        heading: 'Status Flow',
        items: [
          'Pending (yellow pulse) - Waiting to be worked on',
          'In Progress (yellow solid) - Claude is actively working',
          'Validate (blue) - Work done, awaiting your review',
          'Complete (green) - Accepted and finished'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Plus (+) - Add new task',
          'Eye/EyeOff - Toggle completed task visibility',
          'Refresh - Reload from file',
          'Pencil (pending only) - Edit task',
          'Trash (pending only) - Delete task',
          'Eye (validate/complete) - View original request'
        ]
      },
      {
        heading: 'Steps',
        items: [
          '1. Add task via + button or footer quick-add',
          '2. Claude picks up pending tasks and marks In Progress',
          '3. When done, Claude marks Validate for your review',
          '4. Use dropdown: Accept (complete) or Retry (with notes)',
          '5. Double-click Complete tasks to re-open if needed'
        ]
      }
    ]
  },
  'rev-log': {
    title: 'Revision Log',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Version history and release notes',
          'Format: vX.YYY (X=major, YYY=minor)'
        ]
      },
      {
        heading: 'Badge Key',
        items: [
          'MAJ (orange) - Major version: dev plan step completed',
          'MIN (blue) - Minor version: sprint task completed'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Plus (+) - Add new revision',
          'Pencil - Edit selected revision',
          'Save - Save changes',
          'X - Cancel editing',
          'Trash - Delete revision',
          'Refresh - Reload from file'
        ]
      },
      {
        heading: 'Steps',
        items: [
          '1. Select a revision from the list to view details',
          '2. Click Pencil to edit summary or changes',
          '3. Add change entries with + Add Change button',
          '4. Save to persist or X to cancel'
        ]
      }
    ]
  },
  'dev-plan': {
    title: 'Development Plan',
    sections: [
      {
        heading: 'Overview',
        items: [
          '19-step roadmap from foundation to production',
          'Steps 1-4: Foundation (complete)',
          'Steps 5-6: Dev Tools UI, Config Editors',
          'Steps 7-11: Dynamic modules, CRUD, Security',
          'Steps 12+: Auth, APIs, advanced features'
        ]
      },
      {
        heading: 'Status Key',
        items: [
          'Check (green) - Complete',
          'Clock (yellow) - In Progress',
          'Circle (gray) - Not Started'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Plus (+) - Add new step',
          'Pencil - Edit selected step',
          'Save - Save changes',
          'X - Cancel editing',
          'Trash - Delete step',
          'Refresh - Reload from file',
          'Up/Down arrows (detail) - Reorder step position'
        ]
      },
      {
        heading: 'Steps',
        items: [
          '1. Select step from list to view details',
          '2. Click Pencil to enter edit mode',
          '3. Change name, status, description, or notes',
          '4. Click step number to reassign position (non-complete only)',
          '5. Save to persist or X to cancel'
        ]
      }
    ]
  },
  'schema-viewer': {
    title: 'Database Schema',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Live view of database structure from INFORMATION_SCHEMA',
          'Shows all tables with columns, types, and relationships'
        ]
      },
      {
        heading: 'Column Info',
        items: [
          'Table - Source table name',
          'Column - Field name',
          'Data Type - SQL data type',
          'Nullable - NULL or NOT NULL',
          'PK - Primary key indicator',
          'Identity - Auto-increment field',
          'Foreign Key - FK relationship target'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Refresh - Re-query database schema'
        ]
      },
      {
        heading: 'Features',
        items: [
          'Click column headers to sort',
          'Use column dropdowns to filter by specific values',
          'Active filters shown above grid with clear option',
          'Future: Will evolve into Security Manager (Step 11)'
        ]
      }
    ]
  },
  'claude-notes': {
    title: 'PMD Claude Notes',
    sections: [
      {
        heading: 'Overview',
        items: [
          'PMD app-specific documentation for Claude Code sessions',
          'Synced with dev-tracking/PMD-Claude-Notes.md file',
          'Contains architecture, patterns, configs, and session history'
        ]
      },
      {
        heading: 'File Organization',
        items: [
          'CLAUDE.md - People, git rules, approvals, communication style',
          'PMD-Claude-Notes.md - App architecture, patterns, session notes'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Plus (+) - Add timestamped note to Notes section',
          'Refresh - Reload from file'
        ]
      },
      {
        heading: 'Content Sections',
        items: [
          'Quick Start - Session initialization steps',
          'Key Files - Important project files and their purpose',
          'Versioning - Version format and update locations',
          'Development Pages - Available dev tools',
          'Module Config - JSON config structure',
          'Session Notes - Daily work log'
        ]
      }
    ]
  },
  'sample-table-view': {
    title: 'Sample Table View',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Reference implementation for list/detail modules',
          'Demonstrates standard patterns and layouts'
        ]
      },
      {
        heading: 'Components',
        items: [
          'ListPanel - Left-side scrollable item list',
          'Detail Form - Right-side 24-column grid layout',
          'ModuleHeader - View toggle, search, CRUD buttons'
        ]
      },
      {
        heading: 'Usage',
        items: [
          'Use as template when building new table modules',
          'Copy patterns for consistent UI/UX across modules'
        ]
      }
    ]
  },
  'navigation': {
    title: 'Navigation Editor',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Visual editor for sidebar navigation structure',
          'Changes update navigationConfig.js directly'
        ]
      },
      {
        heading: 'Badge Key',
        items: [
          'Future (gray) - Under construction / not built yet',
          'Admin (red) - Only visible to admins',
          'New (green) - Newly added item'
        ]
      },
      {
        heading: 'Buttons (Header)',
        items: [
          'Plus (+) - Add root-level item',
          'Save - Apply changes (creates sprint task)',
          'X - Cancel all changes',
          'Refresh - Reset to current config'
        ]
      },
      {
        heading: 'Row Controls',
        items: [
          'Up/Down arrows - Reorder within siblings',
          'Left arrow - Outdent (move up a level)',
          'Right arrow - Indent (make child of previous)',
          'Plus (+) - Add child item',
          'Trash - Delete item and children'
        ]
      },
      {
        heading: 'Steps',
        items: [
          '1. Expand/collapse items with chevron',
          '2. Use arrows to reorder or change nesting',
          '3. Add new items at root or as children',
          '4. Click Save when done - changes are live'
        ]
      }
    ]
  },
  'standard-view-config': {
    title: 'Standard View Config',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Visual form builder for module configurations',
          'Creates JSON config files in module-configs/'
        ]
      },
      {
        heading: 'Table Icons',
        items: [
          'Green check - Has config file',
          'Blue server - Has API controller',
          'No icons - Needs config and/or API'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Init from DB - Create config from database schema',
          'Save - Write JSON config to file',
          'Create Task - Add module build task to sprint'
        ]
      },
      {
        heading: 'Canvas',
        items: [
          'Drag fields from palette to 24-column grid',
          'Click row Add (+) to insert new row',
          'Double-click or gear icon to edit properties',
          'Use Add Section to create field groupings',
          'X button removes field (returns to palette)'
        ]
      },
      {
        heading: 'Steps',
        items: [
          '1. Select table from dropdown',
          '2. Click Init from DB (or load existing)',
          '3. Arrange fields on canvas by dragging',
          '4. Configure field properties (label, colspan, type)',
          '5. Set List Panel options (primary/secondary line)',
          '6. Save config, then Create Task if needed'
        ]
      }
    ]
  }
};

/**
 * Footer component with Add Task functionality for current sprint (admin only)
 */
const Footer = ({ currentPage, isAdmin }) => {
  const [showAddTask, setShowAddTask] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTask, setNewTask] = useState({ task: '', details: '' });

  // Get status and notify from notification context
  const { status, notify } = useNotifications();

  // Dynamic version from rev log API
  const [appVersion, setAppVersion] = useState('');
  const fetchVersion = () => {
    devFilesApi.getRevLog()
      .then(res => setAppVersion(res.data?.currentVersion || ''))
      .catch(() => setAppVersion(''));
  };
  useEffect(() => {
    fetchVersion();
    window.addEventListener('pmd-version-updated', fetchVersion);
    return () => window.removeEventListener('pmd-version-updated', fetchVersion);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Database context (may be null on admin/dev pages)
  const dbContext = useDatabaseSafe();
  const dbDisplayName = dbContext?.dbDisplayName || '';
  // Always know the database name — from context or localStorage
  const databaseName = dbContext?.currentDb?.databaseName || dbContext?.currentDb?.id || localStorage.getItem('pmd-selected-database') || '';
  const isPreprod = databaseName.toLowerCase().includes('preprod');
  const badgeLabel = dbDisplayName || 'Admin';

  // Check if current page is a dev page
  const isDevPage = DEV_PAGES.includes(currentPage);
  const helpContent = PAGE_HELP[currentPage];

  const getEnteredTimestamp = () => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const mi = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${yy}${mm}${dd}-${hh}${mi}${ss}`;
  };

  const getSourcePage = () => {
    // Convert URL path to readable page name
    const page = currentPage || 'home';
    return page.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleAddTask = async () => {
    if (!newTask.task.trim()) return;

    try {
      setSaving(true);

      // Create task directly via API (server assigns ID)
      await devFilesApi.createTask({
        task: newTask.task.trim(),
        status: 'pending',
        details: newTask.details.trim(),
        source: getSourcePage(),
        owner: null,
        claudeSummary: null
      });

      // Reset form
      setNewTask({ task: '', details: '' });
      setShowAddTask(false);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenModal = () => {
    setNewTask({ task: '', details: '' });
    // error cleared
    setShowAddTask(true);
  };

  return (
    <>
      <footer className="app-footer">
        <div className="footer-left">
          <span
            className={`footer-db-badge ${isPreprod ? 'footer-db-badge-preprod' : ''}`}
            title={databaseName || 'Administration'}
          >
            <Database size={12} />
            <span>{badgeLabel}</span>
          </span>
          {appVersion && <span className={`footer-version ${isPreprod ? 'footer-version-preprod' : ''}`}><ScrollText size={12} />{appVersion}</span>}
          {status && <span className="footer-status">{status}</span>}
        </div>
        <div className="footer-center" />
        <div className="footer-right">
          {isAdmin && (
            <button
              className="footer-add-task-btn"
              onClick={handleOpenModal}
              title="Add task to Current Sprint"
            >
              <ClipboardPlus size={16} />
            </button>
          )}
          {/* Help button - only on dev pages (will expand to all pages later) */}
          {isDevPage && helpContent && (
            <button
              className="footer-help-btn"
              onClick={() => setShowHelp(true)}
              title="How this page works"
            >
              <span className="help-question-mark">?</span>
            </button>
          )}
        </div>
      </footer>

      {/* Add Task Modal */}
      {showAddTask && (
        <div className="footer-add-task-overlay">
          <div className="footer-add-task-modal">
            <div className="modal-header">
              <h3>Add Sprint Task</h3>
              <button className="modal-close" onClick={() => setShowAddTask(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row-inline">
                <div className="form-field">
                  <label>Source Page</label>
                  <input
                    type="text"
                    className="form-input"
                    value={getSourcePage()}
                    readOnly
                    disabled
                  />
                </div>
                <div className="form-field">
                  <label>Entered</label>
                  <input
                    type="text"
                    className="form-input"
                    value={getEnteredTimestamp()}
                    readOnly
                    disabled
                  />
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <input
                    type="text"
                    className="form-input"
                    value="Pending"
                    readOnly
                    disabled
                  />
                </div>
              </div>
              <div className="form-row">
                <label>Task Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={newTask.task}
                  onChange={(e) => setNewTask({ ...newTask, task: e.target.value })}
                  placeholder="Short description of the task"
                  autoFocus
                />
              </div>
              <div className="form-row">
                <label>Task Details</label>
                <textarea
                  className="form-input form-textarea"
                  value={newTask.details}
                  onChange={(e) => setNewTask({ ...newTask, details: e.target.value })}
                  placeholder="Detailed requirements, context, or notes..."
                  rows={5}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddTask(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddTask}
                disabled={saving || !newTask.task.trim()}
              >
                {saving ? 'Adding...' : 'Add Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelp && helpContent && (
        <div className="footer-help-overlay">
          <div className="footer-help-modal">
            <div className="modal-header">
              <h3>{helpContent.title}</h3>
              <button className="modal-close" onClick={() => setShowHelp(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body help-sections">
              {helpContent.sections?.map((section, sIdx) => (
                <div key={sIdx} className="help-section">
                  <h4 className="help-section-heading">{section.heading}</h4>
                  <ul className="help-section-list">
                    {section.items.map((item, iIdx) => (
                      <li key={iIdx}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {/* Fallback for old content format */}
              {!helpContent.sections && helpContent.content && (
                <ol className="help-steps">
                  {helpContent.content.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => setShowHelp(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
